// Google / Microsoft single sign-on through Cognito's OAuth endpoints, using
// the authorization-code flow with PKCE (the SPA's app client has no secret).
//
// There is no Cognito-hosted sign-in page in the middle: the request names the
// provider (identity_provider=Google|Microsoft), so the browser goes straight
// to Google or Microsoft and comes back to /auth/callback with a code.
//
// Who may get in is decided server-side by the PreSignUp trigger
// (backend/src/handlers/pre_signup.py), not here — this file only moves the
// browser and the tokens.

const RAW_DOMAIN = (import.meta.env.VITE_COGNITO_DOMAIN || "").trim();
const DOMAIN = RAW_DOMAIN.replace(/^https?:\/\//, "").replace(/\/+$/, "");
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;

// Display labels for provider names configured on the user pool.
const LABELS = { Google: "Google", Microsoft: "Microsoft" };

/** Providers to offer on the sign-in page, e.g. [{ id: "Google", label: "Google" }]. Empty = SSO off. */
export const SSO_PROVIDERS = DOMAIN
  ? (import.meta.env.VITE_SSO_PROVIDERS || "")
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .map((id) => ({ id, label: LABELS[id] || id }))
  : [];

const KEY = {
  verifier: "sso.verifier",
  state: "sso.state",
  provider: "sso.provider",
  retried: "sso.retried",
};

const callbackUrl = () => `${window.location.origin}/auth/callback`;

function base64Url(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function challengeFor(verifier) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/** Sends the browser to Google or Microsoft. Never returns. */
export async function startSso(provider, { isRetry = false } = {}) {
  const verifier = randomString(48);
  const state = randomString(24);
  sessionStorage.setItem(KEY.verifier, verifier);
  sessionStorage.setItem(KEY.state, state);
  sessionStorage.setItem(KEY.provider, provider);
  if (!isRetry) sessionStorage.removeItem(KEY.retried);

  const qp = new URLSearchParams({
    identity_provider: provider,
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: callbackUrl(),
    scope: "openid email profile",
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`https://${DOMAIN}/oauth2/authorize?${qp.toString()}`);
}

// Cognito wraps a refusal from the PreSignUp trigger as
// "PreSignUp failed with error <message>." — show only the message.
function cleanError(description) {
  const text = (description || "").replace(/\+/g, " ").trim();
  const m = text.match(/PreSignUp failed with error (.*?)\.?\s*$/i);
  return m ? m[1].replace(/\.?$/, ".") : text || "Sign-in was cancelled or failed. Please try again.";
}

let inflight = null;

/**
 * Handles /auth/callback. Resolves to one of:
 *   { tokens }            — exchange succeeded
 *   { retry: provider }   — Cognito's first-link quirk; caller should startSso() again
 * and rejects with a user-facing Error otherwise. Safe to call twice (React
 * StrictMode): the code is exchanged once and the same promise returned.
 */
export function finishSso(search) {
  if (!inflight) inflight = doFinish(search);
  return inflight;
}

async function doFinish(search) {
  const params = new URLSearchParams(search);
  const provider = sessionStorage.getItem(KEY.provider);
  const verifier = sessionStorage.getItem(KEY.verifier);
  const expectedState = sessionStorage.getItem(KEY.state);
  sessionStorage.removeItem(KEY.verifier);
  sessionStorage.removeItem(KEY.state);

  const error = params.get("error_description") || params.get("error");
  if (error) {
    // The sign-in that creates the link between a Google/Microsoft identity
    // and the existing user can fail with this, even though the link was
    // made. Going round once more signs in as the linked user.
    if (/already found an entry for username/i.test(error) && provider && !sessionStorage.getItem(KEY.retried)) {
      sessionStorage.setItem(KEY.retried, "1");
      return { retry: provider };
    }
    throw new Error(cleanError(error));
  }

  const code = params.get("code");
  if (!code || !verifier || !expectedState || params.get("state") !== expectedState) {
    throw new Error("This sign-in link has expired or was already used. Please sign in again.");
  }

  const res = await fetch(`https://${DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: CLIENT_ID,
      code,
      redirect_uri: callbackUrl(),
      code_verifier: verifier,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`Sign-in couldn't be completed (${body.error || res.status}). Please try again.`);
  }
  sessionStorage.removeItem(KEY.retried);
  sessionStorage.removeItem(KEY.provider);
  return { tokens: await res.json() };
}

/** Where to send the browser after a local sign-out, to end the Cognito SSO session too. */
export function ssoLogoutUrl() {
  if (!DOMAIN) return null;
  const qp = new URLSearchParams({ client_id: CLIENT_ID, logout_uri: `${window.location.origin}/login` });
  return `https://${DOMAIN}/logout?${qp.toString()}`;
}
